import { BlueprintContext } from '../blueprint-context/blueprint-context.class';
import { BlueprintInstanceType } from '../shared.enums';
import { emptyReferenceMapCfn, resolveReferenceMapCfn } from './functions';
import type { Notification } from '../notifications/notification.class';
import { toCamelCase } from '../../helpers';

import { ControllerNodeBase, EventPayloadCrud } from '../../controller-classes';
import { InternalReferenceMap, ReferenceMap } from '../shared.types';
import { KitchenDesignOptions, KitchenDesignTypes } from './types';

export class KitchenDesignBase<SerializedDataType> extends ControllerNodeBase<SerializedDataType, BlueprintInstanceType, KitchenDesignTypes, BlueprintContext> {
	protected _refMap: InternalReferenceMap;

	constructor (instanceType: BlueprintInstanceType, options: KitchenDesignOptions<SerializedDataType> = {}) {
		super(
			instanceType,
			options as unknown,
			((options.parent?.context ?? null) ? options.parent.context : new BlueprintContext({ userEmail: 'UNKNOWN' }))
		);

		this._refMap = {
			...emptyReferenceMapCfn(),
			...{ ...(options.parent?.refMap ?? {}), id: () => this.id },
			type: instanceType,
			[toCamelCase(instanceType)]: this
		};
	}
	get parentId (): string | null {
		if (this.instanceParent) {
			if (this.instanceParent.id) {
				return this.instanceParent.id
			} else if (this.instanceParent.instanceParent) {
				if (this.instanceParent.instanceParent.id) {
					return this.instanceParent.instanceParent.id
				}
			}
		}
		return null
	}

	// eslint-disable-next-line class-methods-use-this
	get hasValidationRules (): boolean { return false; }
	get refMap (): ReferenceMap { return resolveReferenceMapCfn(this._refMap); }

	// public findParentInstanceOf <P> (typeName: BlueprintInstanceType, depth = 0): P | null {
	// 	const instanceParent = (this.instanceParent ?? null) as KitchenDesignBase;
	// 	let instance = null;

	// 	if (instanceParent !== null) {
	// 		if (instanceParent.instanceOf === typeName) {
	// 			instance = instanceParent as P;
	// 		} else {
	// 			instance = instanceParent.findParentInstanceOf(typeName, (depth + 1)) as P;
	// 		}
	// 	}

	// 	// console.log(`findParentInstanceOf (return ${depth})`, typeName, this.instanceOf, `${instanceParent.instanceOf} -> ${this.instanceOf}`);
	// 	return instance;
	// }

	public getReferenceMap (id: string, propertiesWithChildren: Array<string> = []): ReferenceMap {
		if (id === null || id === '') {
			// console.log(`getReferenceMap (base:${this.instanceOf})`, 'no id provided');
			return null;
		}

		if (this._refMap === null) {
			// console.log(`getReferenceMap (base:${this.instanceOf})`, 'no refMap');
			return null;
		}

		// if (this.instanceOf === 'connection') console.log(`--> getReferenceMap looking in ${this.instanceOf}`, id, this.id, this);
		let referenceMap: ReferenceMap = null;

		if ('children' in this) {
			// does the id exist as a child record
			let foundNode = (this.children as Array<KitchenDesignBase<SerializedDataType>>).find((p) => p.id === id)
			if (foundNode) {
				referenceMap = foundNode.refMap;
			} else {
				// traverse each child to see if they contain the id
				foundNode = (this.children as Array<KitchenDesignBase<SerializedDataType>>).find((p) => {
					referenceMap = p.getReferenceMap(id);
					return referenceMap !== null;
				});
			}
		}

		if (!referenceMap) {
			propertiesWithChildren.find((childProperty) => {
				// if (this.instanceOf === 'island') console.log(childProperty, this[childProperty]?.getReferenceMap(id));

				const childObject = this[childProperty];
				if (childObject && typeof childObject.getReferenceMap === 'function') {
					referenceMap = childObject.getReferenceMap(id);
				} else if (childObject) {
					console.warn(`**** Property ${childProperty} does not have getReferenceMap method ***`, childObject);
				} else {
					console.warn('**** Property Not Found ***', childProperty, this);
				}
				return referenceMap !== null;
			})
		}

		// if (referenceMap) console.log(`=== getReferenceMap (found in ${this.instanceOf})`, referenceMap);
		return referenceMap;
	}

	public cleanup (): void {
		const payload: EventPayloadCrud = {
			ref: this.refMap,
			old: this.serialize(),
			new: null
		};
		this.publish(this.instanceOf, 'deleted', payload);
	}

	public getNotications (): Array<Notification> {
		return this.refMap.kitchen?.design?.notifications?.filterByRefId(this.id);
	}

	protected setInitialized (): void {
		const payload: EventPayloadCrud = {
			ref: this.refMap,
			old: null,
			new: this.serialize()
		};

		super.setInitialized();

		const eventAction = this.isNew ? 'created' : 'loaded';
		// console.log(`Publish Method -> ${this.instanceOf} (payload)`, eventAction, payload);
		this.publish(this.instanceOf, eventAction, payload);
	}

	// protected serialize (): SerializedDataType { return { id: this.id }; }
}
